import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCatalog } from "../context/CatalogContext";
import { designUrl } from "../data/catalog";
import { matchDesigns } from "../utils/matchDesigns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Deterministic per-browser session id so multi-turn history sticks across
// tab reloads. Falls back to sessionStorage if localStorage is denied.
const getOrMakeSessionId = () => {
  const KEY = "alankar_concierge_session";
  try {
    let sid = localStorage.getItem(KEY);
    if (!sid) {
      sid = `sc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(KEY, sid);
    }
    return sid;
  } catch (_) {
    return `sc-fallback-${Date.now()}`;
  }
};

// Turn any /collections/... /style/... /wishlist bare URLs the AI produces
// into clickable React-Router links. Keeps replies conversational without
// forcing markdown parsing on the frontend.
const LINK_REGEX = /(\/(?:collections|style|wishlist|design)\/[a-z0-9/_-]+)/gi;
function Linkify({ text }) {
  const parts = [];
  let last = 0;
  let m;
  const re = new RegExp(LINK_REGEX);
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a
        key={`l-${m.index}`}
        href={m[0]}
        className="sc-link"
        data-testid="sc-link"
      >
        {m[0]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

const OPENING_TIP = "Hi 👋 I'm Bob, Alankar's shopper concierge. Ask me to match a saree, a budget, or an occasion and I'll suggest pieces from our collection.";

export default function ShopperConcierge() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: OPENING_TIP }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const sessionRef = useRef(getOrMakeSessionId());
  const listRef = useRef(null);
  const { allDesignsMerged } = useCatalog();

  // Auto-scroll to the bottom whenever new content arrives.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = useCallback(async () => {
    const q = input.trim();
    if (!q || streaming) return;
    setInput("");
    setError("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/chat/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: sessionRef.current }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        // Parse SSE frames — each event is separated by a blank line.
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          if (frame.startsWith("event: done")) {
            done = true;
            break;
          }
          if (frame.startsWith("event: error")) {
            const msg = frame.split("data:")[1]?.trim() || "AI service error";
            throw new Error(msg);
          }
          // Multiple `data:` lines can appear in one frame; the backend
          // sends one, but be permissive.
          const dataLines = frame
            .split("\n")
            .filter((l) => l.startsWith("data:"))
            .map((l) => l.slice(5).replace(/^ /, ""));
          if (!dataLines.length) continue;
          const piece = dataLines.join("").replace(/\\n/g, "\n");
          setMessages((m) => {
            const next = [...m];
            const last = next[next.length - 1];
            if (last && last.role === "assistant") {
              next[next.length - 1] = { ...last, text: last.text + piece };
            }
            return next;
          });
        }
      }
    } catch (e) {
      setError(e?.message || "Chat unavailable — please try again.");
      // Roll back the empty placeholder so the UI doesn't show a blank bubble.
      setMessages((m) => {
        const next = [...m];
        if (next.length && next[next.length - 1].role === "assistant" && !next[next.length - 1].text) {
          next.pop();
        }
        return next;
      });
    } finally {
      setStreaming(false);
      // Once the reply is in, compute 2-3 product suggestions from the merged
      // catalog based on the visitor's original question. The concierge stays
      // conversational; the cards render right below the bubble as tappable
      // shortcuts to real product pages.
      const picks = matchDesigns(q, allDesignsMerged, 3);
      if (picks.length) {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, suggestions: picks };
          }
          return next;
        });
      }
    }
  }, [input, streaming, allDesignsMerged]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating trigger — bottom-left so it doesn't overlap WhatsApp */}
      <motion.button
        type="button"
        className="sc-trigger"
        onClick={() => setOpen((v) => !v)}
        data-testid="sc-trigger"
        aria-label={open ? "Close concierge chat" : "Open concierge chat"}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 220, damping: 18 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
      >
        <Sparkles size={20} />
        <span className="sc-trigger-lbl">Ask Bob</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="sc-panel"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            data-testid="sc-panel"
          >
            <header className="sc-head">
              <div>
                <span className="sc-eyebrow">AI Concierge</span>
                <strong>Bob · Alankar</strong>
              </div>
              <button
                type="button"
                className="sc-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="sc-close"
              >
                <X size={18} />
              </button>
            </header>

            <div className="sc-messages" ref={listRef} data-testid="sc-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`sc-msg sc-msg-${m.role}`}
                  data-testid={`sc-msg-${m.role}-${i}`}
                >
                  <Linkify text={m.text} />
                  {m.suggestions && m.suggestions.length > 0 && (
                    <div className="sc-suggestions" data-testid={`sc-suggestions-${i}`}>
                      {m.suggestions.map((d) => (
                        <Link
                          key={d.id}
                          to={designUrl(d)}
                          className="sc-suggestion"
                          onClick={() => setOpen(false)}
                          data-testid={`sc-suggestion-${d.id}`}
                        >
                          <div className="sc-suggestion-media">
                            <img src={d.image} alt={d.name} loading="lazy" />
                          </div>
                          <div className="sc-suggestion-body">
                            <strong>{d.styleName || d.name}</strong>
                            <span>
                              {d.subCategory.label} · {d.purity || d.category.label}
                              {d.weight ? ` · ${d.weight}${d.weightUnit || "g"}` : ""}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {streaming && (
                <div className="sc-msg sc-msg-assistant sc-msg-typing" data-testid="sc-typing">
                  <Loader2 size={14} className="spin" /> Bob is typing…
                </div>
              )}
              {error && (
                <div className="sc-msg sc-msg-error" data-testid="sc-error">
                  {error}
                </div>
              )}
            </div>

            <form
              className="sc-input"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                type="text"
                placeholder="Match my red saree · gold rings under 8g · gift for mum…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                disabled={streaming}
                data-testid="sc-input"
                autoFocus
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="sc-send"
                aria-label="Send"
              >
                {streaming ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
              </button>
            </form>

            <div className="sc-foot">
              Not sure? Call{" "}
              <a href="tel:+919503650463" data-testid="sc-call">
                +91 95036 50463
              </a>{" "}
              — we love a chat.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
